// Every client polling interval, backoff, poll timeout and attempt cap that drives network
// traffic, in one dependency-free module (no imports, no React, no Prisma, no "use client").
//
// Why one module: the load generator (Lasttest/) imports this file directly, so the
// traffic it generates always follows the cadence the site really has instead of a copy
// that drifts. All heartbeat intervals (desktop and phone) live in ./heartbeat-timing.ts
// (same reason) and are not repeated here. klient-intervall.test.ts locks every value: change one on purpose
// and the test tells you to update the generator's profiles too.
//
// All values are milliseconds unless the name says otherwise. One value used in several
// places with different meaning has one name per place.

// ── Student bar: /api/student/aktiv-lektion (LektionsBarForElev) ──────────────────────────
// Since 2026-11-02 the bar LONG-WAITS (?w=1&v=, LIVE_* below) on its class's signal (lib/klass-signal.ts) in a
// visible tab: about one request per LIVE_VANTAN_MAX_MS, the popup within a second of the teacher's start. The
// intervals below are the fallback (hidden tab, live wait switched off after trouble, no class to wait on).
/** No active lesson. */
export const AKTIV_LEKTION_IDLE_MS = 60_000;
/** Active lesson, nothing that needs a tighter poll. */
export const AKTIV_LEKTION_LEKTION_MS = 15_000;
/** A game is running and the student has not joined. */
export const AKTIV_LEKTION_SPEL_MS = 5_000;
/** A genomgang is running and the student is not on the genomgang page. */
export const AKTIV_LEKTION_GENOMGANG_MS = 5_000;
/** The student is on the running genomgang's own page — or, since 2026-10-31, on the page of a game, Begreppsjakt
 *  or Matte-detektiven she has joined: that page's poll already follows the activity, and its end kicks the bar at
 *  once (LektionsBarForElev, "vektr:aktiv-lektion-nu") — so the bar only keeps the lesson's times and an activity
 *  started meanwhile up to date. */
export const AKTIV_LEKTION_I_GENOMGANG_MS = 30_000;
/** Floor on the wait after a pathname change or a rescheduled tick, so nav rendering does not compete with the poll. */
export const AKTIV_LEKTION_NAV_PAUS_MS = 1_500;

// ── Prov: /api/prov-elev/aktivt ───────────────────────────────────────────────────────────
/** ProvVakt, no active pass yet (also when the tab is hidden). */
export const PROVVAKT_MS = 30_000;
/** ProvVakt, an active pass is running, or the student is on a prov page (not the result). */
export const PROVVAKT_AKTIVT_PASS_MS = 6_000;
/** ProvVakt on a prov page: how long after the page opened, or after an answer last named a
 *  pass, it keeps PROVVAKT_AKTIVT_PASS_MS (covers the shared set's TTL right after a start). */
export const PROVSIDA_FONSTER_MS = 30_000;

// ── Mobile pairing ────────────────────────────────────────────────────────────────────────
/** MobileQrButton: is the desktop still paired (current pairing)? */
export const MOBIL_PAR_CURRENT_MS = 30_000;
/** MobileQrModal: the open QR modal asks whether the phone has scanned it (setInterval). */
export const MOBIL_PAR_STATUS_MS = 2_000;
/** MobileQrModal: how long "Ansluten" shows before the modal closes and re-reads current. */
export const MOBIL_PAR_STANG_MS = 1_500;

// ── Scan result poll on the desktop (ScanLosning + resultatPoll) ──────────────────────────
/** While the task is pending (the student is still aiming the camera). */
export const SCAN_RESULTAT_VANTANDE_MS = 2_500;
/** Once the server is grading. */
export const SCAN_RESULTAT_RATTAS_MS = 400;
/** Hard deadline for the whole result poll. */
export const SCAN_RESULTAT_MAX_MS = 90_000;
/** One result request is abandoned after this. */
export const SCAN_RESULTAT_ANROP_TIMEOUT_MS = 20_000;
/** The result route's live wait (?w=1, lib/scan-vantan.ts): well under the request timeout above, which also covers
 *  the route's second run after a change. */
export const SCAN_VANTAN_MAX_MS = 15_000;
/** Default upper bound for the error backoff in startaPoll. */
export const RESULTAT_POLL_MAX_BACKOFF_MS = 5_000;

// ── Scan camera on the phone: /api/scan/task-status (ScanCameraClient) ────────────────────
export const SCAN_TASK_STATUS_MS = 2_000;
/** After a successful upload the phone shows "Klart" this long, then goes back to /m. */
export const SCAN_TILLBAKA_EFTER_UPPLADDNING_MS = 1_200;
export const SCAN_TASK_STATUS_MAX_MS = 360_000;
/** The genomgång's and Matte-detektiven's photo answer (skicka-svar.ts): the computer waits for the phone or
 *  tablet to hand the photo in (/api/scan/task-status). Was a local constant until 2026-10-31. */
export const FOTO_VANTA_TASK_MS = 2_000;
export const SCAN_TASK_STATUS_ANROP_TIMEOUT_MS = 20_000;

// ── Phone long poll: client side (MobileScanWatcher) ──────────────────────────────────────
/** The tab is hidden: wait before looking again. */
export const MOBIL_VANTAN_DOLD_FLIK_MS = 1_500;
/** After a lock-only answer: pause so two instances that disagree cannot spin. */
export const MOBIL_VANTAN_LAS_PAUS_MS = 1_000;
/** After a server error (not 200/204/401/403). */
export const MOBIL_VANTAN_SERVERFEL_BACKOFF_MS = 3_000;
/** After a network error. */
export const MOBIL_VANTAN_NATFEL_BACKOFF_MS = 2_000;

// ── Phone long poll: server side (/api/mobile-pair/wait-for-scan) ─────────────────────────
/** Väg 1 cadence: the database turn when the Runtime Cache signal cannot be trusted. */
export const MOBIL_VANTAN_DB_MS = 2_000;
/** Outside a lesson (?lugn=1): both the signal read and the database turn. */
export const MOBIL_VANTAN_LUGN_MS = 5_000;
/** Väg 2: how often the signal is read in a lesson. */
export const MOBIL_VANTAN_SIGNAL_MS = 1_000;
/** A database turn at least this often even without a signal. */
export const MOBIL_VANTAN_RESERV_MS = 15_000;
/** Length of one long poll. */
export const MOBIL_VANTAN_MAX_MS = 25_000;
/** Length of one long poll for a student in an active prov. */
export const MOBIL_VANTAN_PROV_MAX_MS = 8_000;

// ── Game state poll (useSpelState) ────────────────────────────────────────────────────────
export const SPEL_LARARE_MS = 2_000;
/** Student: base interval; the actual wait is SPEL_ELEV_MS + random * SPEL_ELEV_SPRIDNING_MS. */
export const SPEL_ELEV_MS = 2_300;
export const SPEL_ELEV_SPRIDNING_MS = 200;
/** SPEL_ELEV_MS + SPEL_ELEV_SPRIDNING_MS: the longest student wait (the generator uses it). */
export const SPEL_ELEV_MAX_MS = 2_500;
/** Student with a hidden tab. */
export const SPEL_DOLD_MS = 8_000;
/** Student while answers or choices are accepted (elev-pollintervall.ts → spelFasAktiv): base + random spread. */
export const SPEL_ELEV_AKTIV_MS = 1_400;
export const SPEL_ELEV_AKTIV_SPRIDNING_MS = 200;
/** SPEL_ELEV_AKTIV_MS + SPEL_ELEV_AKTIV_SPRIDNING_MS. */
export const SPEL_ELEV_AKTIV_MAX_MS = 1_600;

// ── Genomgang state poll (useGenomgangState) ──────────────────────────────────────────────
export const GENOMGANG_LARARE_MS = 2_000;
/** Student: base interval; the actual wait is GENOMGANG_ELEV_MIN_MS + random * GENOMGANG_ELEV_SPRIDNING_MS. */
export const GENOMGANG_ELEV_MIN_MS = 2_300;
export const GENOMGANG_ELEV_SPRIDNING_MS = 200;
/** GENOMGANG_ELEV_MIN_MS + GENOMGANG_ELEV_SPRIDNING_MS: the longest student wait. */
export const GENOMGANG_ELEV_MAX_MS = 2_500;
/** Student with a hidden tab. */
export const GENOMGANG_ELEV_DOLD_MS = 10_000;
/** Student while the question is open for answers (elev-pollintervall.ts → genomgangFasAktiv). */
export const GENOMGANG_ELEV_AKTIV_MIN_MS = 1_400;
export const GENOMGANG_ELEV_AKTIV_SPRIDNING_MS = 200;
/** GENOMGANG_ELEV_AKTIV_MIN_MS + GENOMGANG_ELEV_AKTIV_SPRIDNING_MS. */
export const GENOMGANG_ELEV_AKTIV_MAX_MS = 1_600;
/** Student while the slides follow the teacher (elevFoljer, outside "ovning"): every slide change is something the
 *  student waits for, so 2.0 s ± 0.1 s instead of today's 2.3–2.5 s (e2e 2026-10-26: p95 3.05 s on slide changes). */
export const GENOMGANG_ELEV_FOLJ_MIN_MS = 1_900;
export const GENOMGANG_ELEV_FOLJ_SPRIDNING_MS = 200;
/** GENOMGANG_ELEV_FOLJ_MIN_MS + GENOMGANG_ELEV_FOLJ_SPRIDNING_MS. */
export const GENOMGANG_ELEV_FOLJ_MAX_MS = 2_100;
/** Server-side: how stale a genomgang puls may be (genomgang/poll-cache.ts). */
export const GENOMGANG_PULS_CACHE_MS = 500;
/** Teacher: minimum gap between summary requests (useSammanfattningar). */
export const SAMMANFATTNING_MELLANRUM_MS = 30_000;

// ── AI second opinion (ExerciseItem → /api/forsok/omrattning) ────────────────────────────
/** Client timeout of the request; must stay above the server's OMRATTNING_TIMEOUT_MS. */
export const OMRATTNING_ANROP_TIMEOUT_MS = 15_000;

// ── Film viewing ping (FilmSpelare, via film-sedd-konstanter TITTAR_PULS_MS) ──────────────
export const FILM_PULS_MS = 20_000;

// ── Teacher lesson overview (LektionsoversiktClient) and projector (ElevVyClient) ─────────
/** /api/klass/[klassId]/lektion-data (also carries the lesson row's version: lektion-config is read only when it moves). */
export const LARARE_LEKTION_DATA_MS = 10_000;
/** Projector: projektor-lage (config + sections + seating in one poll; three polls until 2026-10-31). */
export const PROJEKTOR_LAGE_MS = 5_000;
/** Projector after the lesson it showed has ended: a new lesson still appears within this. */
export const PROJEKTOR_SLUT_MS = 30_000;

// ── Prov ──────────────────────────────────────────────────────────────────────────────────
/** ProvPassClient (Utföra): the status poll (?vy=status) while the prov is started and the tab is visible. */
export const PROVPASS_BEVAKNING_MS = 10_000;
/** ProvElevClient: how often the student checks whether slutTid has passed; sets the 0-15 s spread of the lamna-in POST. */
export const PROV_SLUTTID_KOLL_MS = 15_000;
/** ProvElevClient (after hand-in) and ProvResultatClient: waiting for the result
 *  (/api/prov-elev/[passId]/resultat) while grading runs — lib/prov/resultat-vantan.ts. */
export const PROV_VANTA_RESULTAT_MS = 10_000;
/** With PROV_VANTA_RESULTAT_MS: the wait's total window (~5 min). */
export const PROV_VANTA_RESULTAT_MAX_FORSOK = 30;
/** Both result waits once grading is done but the result is not released: only the teacher can
 *  release it now (resultatDirekt off), so check once a minute within the same ~5 min window. */
export const PROV_VANTA_SLAPP_MS = 60_000;

// Lesson reflection (Dokument/reflektion-spec.md). Students never poll it: they find it
// through the lesson bar's aktiv-lektion and send their answer once. The teacher's page
// and the projector poll only while it is open and visible, and an unchanged poll costs
// one aggregate ({ oforandrad }).
export const REFLEKTION_LARARE_MS = 5_000;
export const REFLEKTION_PROJEKTOR_MS = 10_000;

// Begreppsjakten (Dokument/begreppsjakt-spec.md). Students poll their own card while the jakt
// runs and the tab is visible (a scan changes the partner's screen); every view shares one
// read of the jakt per instance (BEGREPPSJAKT_LAGE_CACHE_MS), so an unchanged poll costs no
// query. Teacher and projector poll a little faster so a found pair shows at once.
export const BEGREPPSJAKT_ELEV_MS = 2_500;
export const BEGREPPSJAKT_LARARE_MS = 2_000;
export const BEGREPPSJAKT_PROJEKTOR_MS = 2_000;
export const BEGREPPSJAKT_LAGE_CACHE_MS = 800;

// Matte-detektiven (Dokument/matte-detektiven-spec.md). Starta / Visa lösning / Nästa / Avsluta /
// +1 min must reach every student and the projector within one poll (spec: ≤ 2.5 s), so all three
// poll every 2 s while the activity runs and the tab is visible. Every view shares one read of the
// activity per instance (DETEKTIV_LAGE_CACHE_MS), so an unchanged poll costs no query. The shared
// read is kept shorter than Begreppsjakten's (500 vs 800 ms) so a change still reaches a 2 s poll
// well inside the 2.5 s budget.
export const DETEKTIV_ELEV_MS = 2_000;
export const DETEKTIV_LARARE_MS = 2_000;
export const DETEKTIV_PROJEKTOR_MS = 2_000;
export const DETEKTIV_LAGE_CACHE_MS = 500;

// Problemlösning (Dokument/problemlosning-spec.md). The same budget as Matte-detektiven: Starta / Visa
// lösningar / Nästa omgång / Avsluta reach every student and the projector within one poll (≤ 2.5 s; with the
// live wait 0.1–0.6 s), every view shares one read of the activity per instance (an unchanged poll costs no
// query), and the shared read lives 500 ms so a change still reaches a 2 s poll inside the budget.
export const PROBLEMLOSNING_ELEV_MS = 2_000;
export const PROBLEMLOSNING_LARARE_MS = 2_000;
export const PROBLEMLOSNING_PROJEKTOR_MS = 2_000;
export const PROBLEMLOSNING_LAGE_CACHE_MS = 500;

// ── The live wait (lib/live-vantan.ts, Dokument/genomgang-spel-latens-plan.md §6.1, polling-analysen D) ──────
/** A waiting poll's length on the server (under proxy/NAT limits; the phone's long poll runs 25 s in prod). */
export const LIVE_VANTAN_MAX_MS = 20_000;
/** The watcher's tick per instance and key (the cost lever: 500 ms halves the cache reads, +0.13 s p50). */
export const LIVE_SIGNAL_MS = 250;
/** A puls read per instance and key at least this often without a signal (a lost signal ≈ today's worst case). */
export const LIVE_RESERV_MS = 3_000;
/** The puls cadence while the signal reads as null. */
export const LIVE_UTAN_SIGNAL_MS = 1_000;
/** A change in the puls key alone (answers, presence) ends a wait no sooner than this; a version change at once. */
export const LIVE_PULS_MIN_MS = 2_000;
/** Nulls in a row that open the watcher's breaker, and how long it stays open (reads the puls on the null cadence). */
export const LIVE_BRYTARE_GRANS = 5;
export const LIVE_BRYTARE_MS = 30_000;
/** The client's pause between two waits, plus up to LIVE_PAUS_SPRIDNING_MS at random so a class is not in step. */
export const LIVE_PAUS_MIN_MS = 100;
export const LIVE_PAUS_SPRIDNING_MS = 150;
/** The client's own timeout for one waiting request. */
export const LIVE_ANROP_TIMEOUT_MS = 28_000;
/** After 3 failed waits in a row (network, timeout, 5xx) — or a wait that answers too soon too often — the hook
 *  polls the old way for this long. */
export const LIVE_AV_EFTER_FEL_MS = 300_000;
